import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Task } from '../types';
import { countUpdatedTasks, isTaskUpdatedSinceVisit } from '../utils/activityIndicators';
import {
  fetchProjectActivity,
  getCachedProjectAck,
  setCachedTaskSeenAt,
  syncAcknowledgeProject,
  syncTaskSeen,
} from '../utils/activityStateStore';
import {
  beginProjectVisit,
  cancelScheduledEndProjectVisit,
  getActiveVisitBaseline,
  setActiveVisitBaseline,
  setLastProjectVisit,
  setTaskSeen,
} from '../utils/projectVisitPreferences';

interface ProjectActivityContextValue {
  isTaskUpdated: (task: Task) => boolean;
  markTaskSeen: (taskId: string) => void;
  markAllSeen: () => void;
  updatedTaskCount: number;
  hasUnreadActivity: boolean;
  activityLoaded: boolean;
}

const ProjectActivityContext = createContext<ProjectActivityContextValue | null>(null);

export function useProjectActivityState(
  projectId: string,
  userId: string | undefined,
  tasks: Task[],
): ProjectActivityContextValue {
  const lastVisitRef = useRef<Date | null>(null);
  const [seenVersion, setSeenVersion] = useState(0);
  const [activityLoaded, setActivityLoaded] = useState(false);

  useEffect(() => {
    if (!userId || !projectId) return;

    let cancelled = false;
    setActivityLoaded(false);
    cancelScheduledEndProjectVisit(userId, projectId);
    beginProjectVisit(userId, projectId);

    fetchProjectActivity(projectId)
      .then(() => {
        if (cancelled) return;
        lastVisitRef.current = getActiveVisitBaseline(userId, projectId);
        setActivityLoaded(true);
        setSeenVersion((v) => v + 1);
      })
      .catch(() => {
        if (cancelled) return;
        lastVisitRef.current = getActiveVisitBaseline(userId, projectId);
        setActivityLoaded(true);
        setSeenVersion((v) => v + 1);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, userId]);

  const isTaskUpdated = useCallback(
    (task: Task) => isTaskUpdatedSinceVisit(task, lastVisitRef.current, userId),
    [userId, seenVersion],
  );

  const applyAckBaseline = useCallback(
    (at: Date) => {
      lastVisitRef.current = at;
      setActiveVisitBaseline(userId, projectId, at);
    },
    [projectId, userId],
  );

  const markTaskSeen = useCallback(
    (taskId: string) => {
      const now = new Date();
      setTaskSeen(userId, taskId, now);

      void syncTaskSeen(taskId)
        .then((result) => {
          if (!result.hasUnreadActivity) {
            const ack = getCachedProjectAck(projectId) ?? now;
            applyAckBaseline(ack);
          }
          setSeenVersion((v) => v + 1);
        })
        .catch(() => {
          setSeenVersion((v) => v + 1);
        });
    },
    [applyAckBaseline, projectId, userId],
  );

  const markAllSeen = useCallback(() => {
    void syncAcknowledgeProject(projectId)
      .then(() => {
        const ack = getCachedProjectAck(projectId) ?? new Date();
        setLastProjectVisit(userId, projectId, ack);
        applyAckBaseline(ack);
        for (const task of tasks) {
          setCachedTaskSeenAt(task._id, ack);
        }
        setSeenVersion((v) => v + 1);
      })
      .catch(() => {
        setSeenVersion((v) => v + 1);
      });
  }, [applyAckBaseline, projectId, tasks, userId]);

  const updatedTaskCount = useMemo(
    () => countUpdatedTasks(tasks, lastVisitRef.current, userId),
    [tasks, userId, seenVersion],
  );

  return useMemo(
    () => ({
      isTaskUpdated,
      markTaskSeen,
      markAllSeen,
      updatedTaskCount,
      hasUnreadActivity: updatedTaskCount > 0,
      activityLoaded,
    }),
    [activityLoaded, isTaskUpdated, markAllSeen, markTaskSeen, updatedTaskCount],
  );
}

interface ProjectActivityProviderProps {
  projectId: string;
  userId: string | undefined;
  tasks: Task[];
  children: React.ReactNode;
}

export function ProjectActivityProvider({
  projectId,
  userId,
  tasks,
  children,
}: ProjectActivityProviderProps) {
  const value = useProjectActivityState(projectId, userId, tasks);

  return (
    <ProjectActivityContext.Provider value={value}>{children}</ProjectActivityContext.Provider>
  );
}

export function useProjectActivity(): ProjectActivityContextValue | null {
  return useContext(ProjectActivityContext);
}
